import { useState, useEffect, useRef } from "react";

// ─── MODULE CONFIG ────────────────────────────────────────────────────────────
const MODULES = {
  salesTeam: {
    id: "salesTeam",
    label: "Sales Team",
    icon: "👥",
    color: "#6366F1",
    light: "#EEF2FF",
    border: "#C7D2FE",
  },
  retailSales: {
    id: "retailSales",
    label: "Retail Sales",
    icon: "🛒",
    color: "#0EA5E9",
    light: "#E0F2FE",
    border: "#BAE6FD",
  },
  primarySales: {
    id: "primarySales",
    label: "Primary Sales",
    icon: "📦",
    color: "#10B981",
    light: "#ECFDF5",
    border: "#A7F3D0",
  },
  hr: {
    id: "hr",
    label: "HR",
    icon: "🧑‍💼",
    color: "#F59E0B",
    light: "#FFFBEB",
    border: "#FDE68A",
  },
  product: {
    id: "product",
    label: "Product",
    icon: "🏷️",
    color: "#EC4899",
    light: "#FDF2F8",
    border: "#FBCFE8",
  },
};

// ─── DATA ─────────────────────────────────────────────────────────────────────
const salesTeamData = {
  overview: [
    { label: "Total Employees", value: 284, icon: "🏢", accent: "gray" },
    { label: "Active", value: 251, sub: "88.4%", icon: "✅", accent: "green" },
    { label: "Inactive", value: 33, sub: "11.6%", icon: "⏸️", accent: "amber" },
    {
      label: "Present Today",
      value: 218,
      sub: "+3 vs yesterday",
      icon: "🟢",
      accent: "green",
    },
    { label: "On Leave", value: 19, icon: "🏖️", accent: "blue" },
    {
      label: "New Joiners",
      value: 12,
      sub: "This month",
      icon: "🆕",
      accent: "blue",
    },
    {
      label: "Resigned",
      value: 5,
      sub: "This month",
      icon: "👋",
      accent: "red",
    },
    {
      label: "Not Reporting",
      value: 14,
      sub: "Action needed",
      icon: "⚠️",
      accent: "amber",
    },
  ],
  roles: [
    { name: "ASM", count: 28, color: "#7C6FE0" },
    { name: "RSM", count: 18, color: "#3B82F6" },
    { name: "SO", count: 56, color: "#10B981" },
    { name: "SR", count: 74, color: "#84CC16" },
    { name: "TSI", count: 46, color: "#F59E0B" },
    { name: "Distributor Salesman", count: 62, color: "#F97316" },
  ],
  territory: {
    states: 18,
    districts: 142,
    totalBeats: 1840,
    coveredBeats: 1512,
    unassigned: 328,
  },
  attendance: [
    { label: "Check-In Completed", value: 204, color: "#10B981", icon: "🟢" },
    { label: "Check-Out Pending", value: 37, color: "#F59E0B", icon: "🕐" },
    { label: "Absent", value: 33, color: "#EF4444", icon: "🔴" },
    { label: "Late Check-In", value: 21, color: "#F97316", icon: "⏰" },
  ],
  monitoring: [
    { label: "Employees Online", value: 167, accent: "green", icon: "🟢" },
    { label: "Employees Offline", value: 84, accent: "gray", icon: "⚫" },
    { label: "No Movement", value: 22, accent: "amber", icon: "🔵" },
    { label: "Fake GPS", value: 7, accent: "red", icon: "🚨" },
    { label: "Manual Requests", value: 15, accent: "amber", icon: "📝" },
  ],
  alerts: [
    {
      icon: "🚨",
      label: "Fake GPS detections requiring review",
      value: 7,
      type: "danger",
    },
    {
      icon: "📝",
      label: "Manual attendance requests pending",
      value: 15,
      type: "warn",
    },
    {
      icon: "⚠️",
      label: "Employees not reporting — follow-up needed",
      value: 14,
      type: "warn",
    },
    {
      icon: "🔵",
      label: "No movement detected for over 2 hrs",
      value: 22,
      type: "info",
    },
    {
      icon: "✅",
      label: "New joiners onboarded this month",
      value: 12,
      type: "success",
    },
  ],
};

const retailSalesData = {
  kpis: [
    { label: "Total Outlets", value: 12480, icon: "🏪", accent: "blue" },
    {
      label: "Active Outlets",
      value: 10320,
      sub: "82.7%",
      icon: "✅",
      accent: "green",
    },
    {
      label: "Inactive Outlets",
      value: 2160,
      sub: "17.3%",
      icon: "⛔",
      accent: "red",
    },
    {
      label: "New Outlets MTD",
      value: 148,
      sub: "This month",
      icon: "🆕",
      accent: "blue",
    },
    {
      label: "Outlets Visited Today",
      value: 3840,
      sub: "30.8%",
      icon: "👣",
      accent: "green",
    },
    { label: "Pending Visits", value: 8640, icon: "⏳", accent: "amber" },
    { label: "Orders Placed Today", value: 1920, icon: "🛒", accent: "green" },
    {
      label: "Order Value Today",
      value: "₹18.4L",
      icon: "💰",
      accent: "green",
      raw: true,
    },
  ],
  channelSplit: [
    { name: "General Trade", pct: 54, color: "#6366F1" },
    { name: "Modern Trade", pct: 22, color: "#0EA5E9" },
    { name: "E-Commerce", pct: 14, color: "#10B981" },
    { name: "Institutional", pct: 10, color: "#F59E0B" },
  ],
  topSKUs: [
    { name: "Product A 500ml", units: 4200, revenue: "₹3.8L" },
    { name: "Product B 1L", units: 3600, revenue: "₹4.2L" },
    { name: "Product C 250g", units: 5100, revenue: "₹2.6L" },
    { name: "Product D 1Kg", units: 2800, revenue: "₹5.1L" },
    { name: "Product E 200ml", units: 6400, revenue: "₹1.9L" },
  ],
  alerts: [
    {
      icon: "⚡",
      label: "High-value outlets not visited this week",
      value: 42,
      type: "danger",
    },
    {
      icon: "📉",
      label: "Outlets with declining orders (3 months)",
      value: 218,
      type: "warn",
    },
    {
      icon: "🆕",
      label: "New outlet onboarding requests pending",
      value: 31,
      type: "info",
    },
    {
      icon: "✅",
      label: "Outlets meeting monthly targets",
      value: 6840,
      type: "success",
    },
  ],
};

const primarySalesData = {
  kpis: [
    { label: "Total Distributors", value: 342, icon: "🏭", accent: "green" },
    {
      label: "Active Distributors",
      value: 318,
      sub: "92.9%",
      icon: "✅",
      accent: "green",
    },
    { label: "Orders This Month", value: 2840, icon: "📦", accent: "blue" },
    {
      label: "MTD Primary Sales",
      value: "₹4.2Cr",
      icon: "💰",
      accent: "green",
      raw: true,
    },
    {
      label: "Target Achievement",
      value: "78%",
      icon: "🎯",
      accent: "amber",
      raw: true,
    },
    { label: "Pending Dispatches", value: 124, icon: "🚚", accent: "amber" },
    {
      label: "Credit Overdue",
      value: 38,
      sub: "Distributors",
      icon: "🔴",
      accent: "red",
    },
    { label: "New Distributors MTD", value: 7, icon: "🆕", accent: "blue" },
  ],
  regionPerf: [
    { region: "West", target: 120, achieved: 98, color: "#6366F1" },
    { region: "North", target: 90, achieved: 82, color: "#0EA5E9" },
    { region: "South", target: 110, achieved: 91, color: "#10B981" },
    { region: "East", target: 75, achieved: 54, color: "#F59E0B" },
    { region: "Central", target: 60, achieved: 48, color: "#EC4899" },
  ],
  inventory: [
    { label: "Total SKUs Listed", value: 124, icon: "🏷️", accent: "blue" },
    { label: "Out of Stock SKUs", value: 18, icon: "❌", accent: "red" },
    { label: "Slow Moving SKUs", value: 31, icon: "🐢", accent: "amber" },
    { label: "Near Expiry Batches", value: 9, icon: "⏰", accent: "amber" },
  ],
  alerts: [
    {
      icon: "🔴",
      label: "Distributors with credit overdue >30 days",
      value: 38,
      type: "danger",
    },
    {
      icon: "📉",
      label: "Regions below 70% target achievement",
      value: 2,
      type: "warn",
    },
    {
      icon: "🚚",
      label: "Dispatches pending >3 days",
      value: 24,
      type: "warn",
    },
    {
      icon: "✅",
      label: "Distributors who met monthly target",
      value: 214,
      type: "success",
    },
  ],
};

const hrData = {
  kpis: [
    { label: "Total Headcount", value: 412, icon: "👤", accent: "gray" },
    { label: "Active Employees", value: 389, icon: "✅", accent: "green" },
    { label: "On Probation", value: 24, icon: "🔄", accent: "amber" },
    { label: "Open Positions", value: 18, icon: "📋", accent: "blue" },
    {
      label: "Attrition MTD",
      value: 5,
      sub: "1.2% rate",
      icon: "👋",
      accent: "red",
    },
    {
      label: "Avg Tenure (yrs)",
      value: "3.4",
      icon: "🗓️",
      accent: "blue",
      raw: true,
    },
    { label: "Training Hours MTD", value: 320, icon: "📚", accent: "green" },
    { label: "Pending Appraisals", value: 46, icon: "📊", accent: "amber" },
  ],
  leaveBreakdown: [
    { type: "Casual Leave", used: 148, total: 240, color: "#6366F1" },
    { type: "Sick Leave", used: 92, total: 200, color: "#EF4444" },
    { type: "Earned Leave", used: 204, total: 480, color: "#10B981" },
    { type: "LOP", used: 18, total: null, color: "#F59E0B" },
  ],
  hiring: [
    { label: "Applications Received", value: 184, icon: "📩", accent: "blue" },
    { label: "Interviews Scheduled", value: 42, icon: "📅", accent: "blue" },
    { label: "Offers Extended", value: 11, icon: "📄", accent: "green" },
    { label: "Offers Accepted", value: 9, icon: "✅", accent: "green" },
  ],
  alerts: [
    {
      icon: "📊",
      label: "Appraisals pending manager submission",
      value: 46,
      type: "warn",
    },
    {
      icon: "⏰",
      label: "Contracts expiring within 30 days",
      value: 12,
      type: "danger",
    },
    {
      icon: "📚",
      label: "Employees yet to complete compliance training",
      value: 67,
      type: "warn",
    },
    {
      icon: "✅",
      label: "Employees with full attendance this month",
      value: 211,
      type: "success",
    },
  ],
};

const productData = {
  kpis: [
    { label: "Total SKUs", value: 248, icon: "🏷️", accent: "pink" },
    {
      label: "Active SKUs",
      value: 196,
      sub: "79%",
      icon: "✅",
      accent: "green",
    },
    { label: "New Launches MTD", value: 6, icon: "🚀", accent: "blue" },
    { label: "Discontinued", value: 14, icon: "🗑️", accent: "red" },
    {
      label: "Avg Margin %",
      value: "34%",
      icon: "💹",
      accent: "green",
      raw: true,
    },
    { label: "Price Revisions MTD", value: 8, icon: "🔄", accent: "amber" },
    { label: "Out of Stock", value: 18, icon: "❌", accent: "red" },
    { label: "Near Expiry", value: 9, icon: "⏰", accent: "amber" },
  ],
  categories: [
    { name: "Beverages", skus: 62, revenue: "₹1.4Cr", color: "#6366F1" },
    { name: "Snacks", skus: 48, revenue: "₹0.9Cr", color: "#0EA5E9" },
    { name: "Personal Care", skus: 38, revenue: "₹0.6Cr", color: "#EC4899" },
    { name: "Household", skus: 54, revenue: "₹1.1Cr", color: "#10B981" },
    { name: "Others", skus: 46, revenue: "₹0.7Cr", color: "#F59E0B" },
  ],
  topMovers: [
    { name: "Product A 500ml", units: 42000, trend: "+12%" },
    { name: "Product B 1L", units: 36000, trend: "+8%" },
    { name: "Product C 250g", units: 51000, trend: "+22%" },
    { name: "Product D 1Kg", units: 28000, trend: "-4%" },
  ],
  alerts: [
    {
      icon: "🚀",
      label: "New product launches awaiting listing",
      value: 6,
      type: "info",
    },
    {
      icon: "⏰",
      label: "SKUs with stock expiring in 30 days",
      value: 9,
      type: "danger",
    },
    {
      icon: "❌",
      label: "Out of stock SKUs needing reorder",
      value: 18,
      type: "warn",
    },
    {
      icon: "✅",
      label: "SKUs meeting monthly sell-through target",
      value: 148,
      type: "success",
    },
  ],
};

// ─── SHARED HELPERS ───────────────────────────────────────────────────────────
function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const end = typeof value === "number" ? value : 0;
    let cur = prev.current;
    prev.current = end;
    const step = (end - cur) / (700 / 16);
    const t = setInterval(() => {
      cur += step;
      if ((step >= 0 && cur >= end) || (step < 0 && cur <= end)) {
        setDisplay(end);
        clearInterval(t);
      } else {
        setDisplay(Math.floor(cur));
      }
    }, 16);
    return () => clearInterval(t);
  }, [value]);
  return <>{display.toLocaleString()}</>;
}

const ACCENTS = {
  green: { bg: "#ECFDF5", text: "#059669", border: "#A7F3D0" },
  red: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
  amber: { bg: "#FFFBEB", text: "#D97706", border: "#FDE68A" },
  blue: { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" },
  gray: { bg: "#F8FAFC", text: "#475569", border: "#E2E8F0" },
  pink: { bg: "#FDF2F8", text: "#DB2777", border: "#FBCFE8" },
};

const ALERT_COLORS = {
  danger: {
    bg: "#FEF2F2",
    border: "#FECACA",
    text: "#DC2626",
    iconBg: "#FEE2E2",
  },
  warn: {
    bg: "#FFFBEB",
    border: "#FDE68A",
    text: "#D97706",
    iconBg: "#FEF3C7",
  },
  info: {
    bg: "#EFF6FF",
    border: "#BFDBFE",
    text: "#2563EB",
    iconBg: "#DBEAFE",
  },
  success: {
    bg: "#ECFDF5",
    border: "#A7F3D0",
    text: "#059669",
    iconBg: "#D1FAE5",
  },
};

// ─── UI COMPONENTS ────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent = "gray", icon, raw }) {
  const c = ACCENTS[accent] || ACCENTS.gray;
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "#fff",
        border: `1px solid ${hov ? c.text + "44" : c.border}`,
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        cursor: "default",
        transform: hov ? "translateY(-2px)" : "none",
        boxShadow: hov
          ? `0 4px 16px ${c.border}`
          : "0 1px 3px rgba(0,0,0,0.04)",
        transition: "all .15s",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "#94A3B8",
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: 15 }}>{icon}</span>
      </div>
      <span
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: c.text,
          lineHeight: 1.1,
        }}
      >
        {raw ? value : <AnimatedNumber value={value} />}
      </span>
      {sub && <span style={{ fontSize: 11, color: "#94A3B8" }}>{sub}</span>}
    </div>
  );
}

function AlertRow({ icon, label, value, type }) {
  const c = ALERT_COLORS[type] || ALERT_COLORS.info;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 9,
        padding: "9px 12px",
        marginBottom: 7,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          background: c.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 15,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <span
        style={{ flex: 1, fontSize: 13, color: "#334155", fontWeight: 500 }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: c.text,
          background: c.iconBg,
          padding: "2px 9px",
          borderRadius: 5,
        }}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
    </div>
  );
}

function SecHead({ icon, title, color }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        margin: "22px 0 10px",
        paddingBottom: 8,
        borderBottom: `2px solid ${color}22`,
      }}
    >
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color,
        }}
      >
        {title}
      </span>
    </div>
  );
}

function BarRow({ label, value, max, color }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 0",
        borderBottom: "1px solid #F1F5F9",
      }}
    >
      <span
        style={{
          width: 150,
          fontSize: 12,
          fontWeight: 600,
          color: "#334155",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <div
        style={{
          flex: 1,
          height: 7,
          background: "#F1F5F9",
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            borderRadius: 99,
            transition: "width 1s ease",
          }}
        />
      </div>
      <span
        style={{
          minWidth: 36,
          fontSize: 12,
          fontWeight: 700,
          textAlign: "right",
          color,
          background: color + "18",
          padding: "2px 7px",
          borderRadius: 5,
        }}
      >
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function Card({ children, style = {} }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 10,
        padding: "14px 16px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── PANELS ───────────────────────────────────────────────────────────────────
function SalesTeamPanel() {
  const d = salesTeamData;
  const covPct = Math.round(
    (d.territory.coveredBeats / d.territory.totalBeats) * 100,
  );
  const totalAtt = d.attendance.reduce((s, a) => s + a.value, 0);
  const maxRole = Math.max(...d.roles.map((r) => r.count));

  return (
    <div>
      {/* Overview cards */}
      <SecHead icon="📊" title="Team Overview" color="#6366F1" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.overview.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      {/* Role-wise */}
      <SecHead icon="🏗️" title="Role-wise Count" color="#6366F1" />
      <Card>
        {d.roles.map((r, i) => (
          <BarRow
            key={i}
            label={r.name}
            value={r.count}
            max={maxRole * 1.2}
            color={r.color}
          />
        ))}
      </Card>

      {/* Territory */}
      <SecHead icon="🗺️" title="Territory Coverage" color="#6366F1" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
          marginBottom: 10,
        }}
      >
        <StatCard
          label="Total States"
          value={d.territory.states}
          icon="🌏"
          accent="blue"
        />
        <StatCard
          label="Total Districts"
          value={d.territory.districts}
          icon="📍"
          accent="blue"
        />
        <StatCard
          label="Total Beats"
          value={d.territory.totalBeats}
          icon="🗺️"
          accent="gray"
        />
        <StatCard
          label="Covered Beats"
          value={d.territory.coveredBeats}
          sub={`${covPct}%`}
          icon="✅"
          accent="green"
        />
        <StatCard
          label="Unassigned Beats"
          value={d.territory.unassigned}
          icon="❌"
          accent="red"
        />
      </div>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 6,
            fontSize: 12,
          }}
        >
          <span style={{ fontWeight: 600, color: "#475569" }}>
            Beat Coverage Progress
          </span>
          <span style={{ fontWeight: 700, color: "#059669" }}>{covPct}%</span>
        </div>
        <div
          style={{
            height: 9,
            background: "#F1F5F9",
            borderRadius: 99,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${covPct}%`,
              height: "100%",
              background: "linear-gradient(90deg,#10B981,#34D399)",
              borderRadius: 99,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 5,
            fontSize: 11,
            color: "#94A3B8",
          }}
        >
          <span>✅ {d.territory.coveredBeats.toLocaleString()} covered</span>
          <span>❌ {d.territory.unassigned.toLocaleString()} unassigned</span>
        </div>
      </Card>

      {/* Attendance */}
      <SecHead icon="🗓️" title="Attendance Summary" color="#6366F1" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          {d.attendance.map((a, i) => (
            <StatCard
              key={i}
              label={a.label}
              value={a.value}
              icon={a.icon}
              accent={i === 0 ? "green" : i === 2 ? "red" : "amber"}
            />
          ))}
        </div>
        <Card>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              color: "#94A3B8",
              marginBottom: 10,
            }}
          >
            Split
          </div>
          {d.attendance.map((a, i) => {
            const pct = Math.round((a.value / totalAtt) * 100);
            return (
              <div key={i} style={{ marginBottom: 9 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 3,
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: "#475569", fontWeight: 500 }}>
                    {a.label}
                  </span>
                  <span style={{ fontWeight: 700, color: a.color }}>
                    {a.value}{" "}
                    <span style={{ color: "#94A3B8", fontWeight: 400 }}>
                      ({pct}%)
                    </span>
                  </span>
                </div>
                <div
                  style={{
                    height: 5,
                    background: "#F1F5F9",
                    borderRadius: 99,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      background: a.color,
                      borderRadius: 99,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </Card>
      </div>

      {/* Monitoring */}
      <SecHead icon="📡" title="Monitoring Metrics" color="#6366F1" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.monitoring.map((m, i) => (
          <StatCard key={i} {...m} />
        ))}
      </div>

      {/* Alerts */}
      <SecHead icon="🔔" title="Action Required" color="#6366F1" />
      {d.alerts.map((a, i) => (
        <AlertRow key={i} {...a} />
      ))}
    </div>
  );
}

function RetailSalesPanel() {
  const d = retailSalesData;
  return (
    <div>
      <SecHead icon="📊" title="Outlet KPIs" color="#0EA5E9" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.kpis.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      <SecHead icon="🏬" title="Channel Split" color="#0EA5E9" />
      <Card>
        {d.channelSplit.map((ch, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "7px 0",
              borderBottom:
                i < d.channelSplit.length - 1 ? "1px solid #F1F5F9" : "none",
            }}
          >
            <span
              style={{
                width: 130,
                fontSize: 12,
                fontWeight: 600,
                color: "#334155",
                flexShrink: 0,
              }}
            >
              {ch.name}
            </span>
            <div
              style={{
                flex: 1,
                height: 8,
                background: "#F1F5F9",
                borderRadius: 99,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${ch.pct}%`,
                  height: "100%",
                  background: ch.color,
                  borderRadius: 99,
                }}
              />
            </div>
            <span
              style={{
                minWidth: 36,
                fontSize: 12,
                fontWeight: 700,
                color: ch.color,
                background: ch.color + "18",
                padding: "2px 7px",
                borderRadius: 5,
              }}
            >
              {ch.pct}%
            </span>
          </div>
        ))}
      </Card>

      <SecHead icon="🏆" title="Top SKUs by Volume" color="#0EA5E9" />
      <Card style={{ padding: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 80px 80px",
            background: "#F8FAFC",
            padding: "8px 14px",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          {["SKU", "Units", "Revenue"].map((h) => (
            <span
              key={h}
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                color: "#94A3B8",
                textAlign: h === "SKU" ? "left" : "right",
              }}
            >
              {h}
            </span>
          ))}
        </div>
        {d.topSKUs.map((s, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 80px 80px",
              padding: "9px 14px",
              borderBottom:
                i < d.topSKUs.length - 1 ? "1px solid #F1F5F9" : "none",
            }}
          >
            <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
              {s.name}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                textAlign: "right",
              }}
            >
              {s.units.toLocaleString()}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#059669",
                textAlign: "right",
              }}
            >
              {s.revenue}
            </span>
          </div>
        ))}
      </Card>

      <SecHead icon="🔔" title="Action Required" color="#0EA5E9" />
      {d.alerts.map((a, i) => (
        <AlertRow key={i} {...a} />
      ))}
    </div>
  );
}

function PrimarySalesPanel() {
  const d = primarySalesData;
  return (
    <div>
      <SecHead icon="📊" title="Distributor KPIs" color="#10B981" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.kpis.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      <SecHead icon="🌍" title="Region Performance (₹ Lakh)" color="#10B981" />
      <Card>
        {d.regionPerf.map((r, i) => {
          const pct = Math.round((r.achieved / r.target) * 100);
          return (
            <div
              key={i}
              style={{ marginBottom: i < d.regionPerf.length - 1 ? 14 : 0 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 600, color: "#334155" }}>
                  {r.region}
                </span>
                <span style={{ color: "#475569" }}>
                  ₹{r.achieved}L / ₹{r.target}L &nbsp;
                  <span
                    style={{
                      background: pct >= 80 ? "#D1FAE5" : "#FEF3C7",
                      color: pct >= 80 ? "#059669" : "#D97706",
                      padding: "1px 6px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {pct}%
                  </span>
                </span>
              </div>
              <div
                style={{
                  height: 7,
                  background: "#F1F5F9",
                  borderRadius: 99,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: "100%",
                    background: r.color,
                    borderRadius: 99,
                    transition: "width 1s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </Card>

      <SecHead icon="📦" title="Inventory Health" color="#10B981" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.inventory.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      <SecHead icon="🔔" title="Action Required" color="#10B981" />
      {d.alerts.map((a, i) => (
        <AlertRow key={i} {...a} />
      ))}
    </div>
  );
}

function HRPanel() {
  const d = hrData;
  return (
    <div>
      <SecHead icon="📊" title="Workforce KPIs" color="#F59E0B" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.kpis.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      <SecHead icon="🏖️" title="Leave Breakdown" color="#F59E0B" />
      <Card>
        {d.leaveBreakdown.map((l, i) => (
          <div
            key={i}
            style={{ marginBottom: i < d.leaveBreakdown.length - 1 ? 14 : 0 }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 600, color: "#334155" }}>
                {l.type}
              </span>
              <span style={{ color: "#475569" }}>
                <strong style={{ color: l.color }}>{l.used}</strong>
                {l.total ? ` / ${l.total} days` : " days (LOP)"}
              </span>
            </div>
            {l.total && (
              <div
                style={{
                  height: 7,
                  background: "#F1F5F9",
                  borderRadius: 99,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.round((l.used / l.total) * 100)}%`,
                    height: "100%",
                    background: l.color,
                    borderRadius: 99,
                    transition: "width 1s ease",
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </Card>

      <SecHead icon="🧑‍💼" title="Hiring Pipeline" color="#F59E0B" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.hiring.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      <SecHead icon="🔔" title="Action Required" color="#F59E0B" />
      {d.alerts.map((a, i) => (
        <AlertRow key={i} {...a} />
      ))}
    </div>
  );
}

function ProductPanel() {
  const d = productData;
  const maxSkus = Math.max(...d.categories.map((c) => c.skus));
  return (
    <div>
      <SecHead icon="📊" title="Product KPIs" color="#EC4899" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))",
          gap: 9,
        }}
      >
        {d.kpis.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      <SecHead icon="🗂️" title="Category-wise SKUs" color="#EC4899" />
      <Card>
        {d.categories.map((cat, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "7px 0",
              borderBottom:
                i < d.categories.length - 1 ? "1px solid #F1F5F9" : "none",
            }}
          >
            <span
              style={{
                width: 120,
                fontSize: 12,
                fontWeight: 600,
                color: "#334155",
                flexShrink: 0,
              }}
            >
              {cat.name}
            </span>
            <div
              style={{
                flex: 1,
                height: 7,
                background: "#F1F5F9",
                borderRadius: 99,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.round((cat.skus / maxSkus) * 100)}%`,
                  height: "100%",
                  background: cat.color,
                  borderRadius: 99,
                }}
              />
            </div>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: cat.color,
                background: cat.color + "18",
                padding: "2px 6px",
                borderRadius: 5,
                minWidth: 28,
                textAlign: "center",
              }}
            >
              {cat.skus}
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#059669",
                minWidth: 52,
                textAlign: "right",
              }}
            >
              {cat.revenue}
            </span>
          </div>
        ))}
      </Card>

      <SecHead icon="🏆" title="Top Moving SKUs" color="#EC4899" />
      <Card style={{ padding: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 90px 70px",
            background: "#F8FAFC",
            padding: "8px 14px",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          {["Product", "Units Sold", "Trend"].map((h) => (
            <span
              key={h}
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                color: "#94A3B8",
                textAlign: h === "Product" ? "left" : "right",
              }}
            >
              {h}
            </span>
          ))}
        </div>
        {d.topMovers.map((p, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 90px 70px",
              padding: "9px 14px",
              borderBottom:
                i < d.topMovers.length - 1 ? "1px solid #F1F5F9" : "none",
            }}
          >
            <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
              {p.name}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                textAlign: "right",
              }}
            >
              {p.units.toLocaleString()}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                textAlign: "right",
                color: p.trend.startsWith("+") ? "#059669" : "#DC2626",
              }}
            >
              {p.trend}
            </span>
          </div>
        ))}
      </Card>

      <SecHead icon="🔔" title="Action Required" color="#EC4899" />
      {d.alerts.map((a, i) => (
        <AlertRow key={i} {...a} />
      ))}
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
const PANELS = {
  salesTeam: <SalesTeamPanel />,
  retailSales: <RetailSalesPanel />,
  primarySales: <PrimarySalesPanel />,
  hr: <HRPanel />,
  product: <ProductPanel />,
};

export default function RetailDashboard() {
  const [active, setActive] = useState("salesTeam");
  const [pulse, setPulse] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setPulse((p) => !p), 900);
    return () => clearInterval(t);
  }, []);

  const mod = MODULES[active];
  const now = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div
      style={{
        fontFamily: "'Inter','Segoe UI',sans-serif",
        background: "#F1F5F9",
        minHeight: "100vh",
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          background: "linear-gradient(135deg,#1E293B 0%,#0F172A 100%)",
          padding: "13px 22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 2px 14px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: `linear-gradient(135deg,${mod.color},${mod.color}88)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 17,
              transition: "background .3s",
            }}
          >
            {mod.icon}
          </div>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: "#F8FAFC",
                letterSpacing: "-0.01em",
              }}
            >
              Admin Dashboard
            </div>
            <div style={{ fontSize: 11, color: "#64748B" }}>{now}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#10B981",
              boxShadow: pulse ? "0 0 0 4px rgba(16,185,129,0.25)" : "none",
              transition: "box-shadow .4s",
            }}
          />
          <span style={{ fontSize: 11, color: "#64748B", fontWeight: 600 }}>
            Live
          </span>
        </div>
      </div>

      {/* ── Nav tabs ── */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          overflowX: "auto",
          position: "sticky",
          top: 60,
          zIndex: 90,
          boxShadow: "0 1px 5px rgba(0,0,0,0.05)",
        }}
      >
        {Object.values(MODULES).map((m) => {
          const on = active === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setActive(m.id)}
              onMouseEnter={(e) => {
                if (!on) {
                  e.currentTarget.style.background = "#F8FAFC";
                  e.currentTarget.style.color = m.color;
                }
              }}
              onMouseLeave={(e) => {
                if (!on) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#64748B";
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "0 20px",
                height: 50,
                flexShrink: 0,
                border: "none",
                borderBottom: on
                  ? `3px solid ${m.color}`
                  : "3px solid transparent",
                background: on ? m.light : "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: on ? 700 : 500,
                color: on ? m.color : "#64748B",
                transition: "all .15s",
                outline: "none",
              }}
            >
              <span
                style={{
                  fontSize: 17,
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: on ? m.color + "22" : "transparent",
                  transition: "background .15s",
                }}
              >
                {m.icon}
              </span>
              {m.label}
            </button>
          );
        })}
      </div>

      {/* ── Module banner ── */}
      <div
        style={{
          background: `linear-gradient(135deg,${mod.color}12,${mod.color}04)`,
          borderBottom: `1px solid ${mod.border}`,
          padding: "9px 22px",
          display: "flex",
          alignItems: "center",
          gap: 9,
          transition: "background .3s",
        }}
      >
        <span style={{ fontSize: 18 }}>{mod.icon}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: mod.color }}>
            {mod.label}
          </div>
          <div style={{ fontSize: 11, color: "#94A3B8" }}>
            All metrics updated in real-time
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div
        style={{ maxWidth: 1020, margin: "0 auto", padding: "4px 18px 44px" }}
      >
        {PANELS[active]}
      </div>
    </div>
  );
}
